@if (!form) {
  <cor-skeleton [type]="SKELETON_TYPES.RUN_JOB_DETAILS" />
} @else {
  <div
    [ngClass]="{
      'tw:p-3': [VIEW_TYPE.RERUN, VIEW_TYPE.NEW, VIEW_TYPE.RERUN_REPORTS] | includes: viewType(),
    }">
    @if (showHeaderSection()) {
      <div
        class="tw:py-2 tw:px-5 tw:sticky tw:top-0 tw:z-10 tw:bg-(--p-content-background) tw:rounded-xl tw:mb-2"
        test-id="name-bar">
        <div class="row">
          <div class="col-12 tw:flex tw:justify-between">
            @if (!form) {
              <cor-skeleton [type]="SKELETON_TYPES.TAB_TITLE" />
            } @else {
              <div class="tw:flex tw:flex-auto">
                <cor-input-editor
                  class="tw:flex tw:flex-auto tw:leading-relaxed"
                  [formControl]="form.controls.descriptionFormGroup.controls.name"
                  test-id="monitoring-job-name" />
              </div>
            }

            <p-button
              test-id="run-job-details-cancel"
              [buttonProps]="{ autofocus: false }"
              icon="pi pi-times-circle"
              label="Cancel"
              severity="danger"
              variant="outlined"
              (click)="redirectToJobHistory()" />
          </div>
        </div>
      </div>
    }
    @if (isExisitingJob() && jobData()) {
      <section
        name="progressinfo"
        class="tw:mb-3">
        <p-card
          header="Information"
          [style]="{ 'margin-bottom': '0.75rem' }">
          @if (!isJobDataLoaded()) {
            <cor-skeleton [type]="SKELETON_TYPES.JOB_INFORMATION" />
          } @else {
            <div class="tw:flex tw:flex-col tw:gap-2">
              <div class="tw:flex tw:flex-row">
                <div class="col-4">
                  <label>Current Status</label>
                </div>
                <div class="col-8 tw:flex tw:items-center">
                  <cor-status-icon [status]="jobData().jobStatus" />
                  @if (
                    jobData().jobStatus === SCHEDULED_JOB_STATUS.QUEUED &&
                    jobData().queuePosition > 0
                  ) {
                    <a
                      test-id="run-job-details-view-all-background-jobs"
                      routerLink="/job-board"
                      pTooltip="View all background jobs"
                      tooltipPosition="bottom"
                      class="tw:ms-1">
                      {{ `at position ${jobData().queuePosition}` }}
                    </a>
                  }
                  @if (
                    [
                      SCHEDULED_JOB_STATUS.RUNNING,
                      SCHEDULED_JOB_STATUS.COMPILING,
                      SCHEDULED_JOB_STATUS.QUEUED,
                      SCHEDULED_JOB_STATUS.SCHEDULED,
                    ] | includes: jobData().jobStatus
                  ) {
                    <i
                      test-id="run-job-details-refresh"
                      class="pi pi-sync tw:ms-2 tw:cursor-pointer"
                      (click)="refreshSimulation()"
                      pTooltip="Refresh"
                      aria-hidden="true"></i>
                  }
                  @if (jobData().hasErrors) {
                    <p-button
                      test-id="run-job-details-view-logs"
                      [buttonProps]="{ autofocus: false }"
                      (click)="showLogsEvent.emit()"
                      variant="text"
                      severity="primary"
                      size="small"
                      label="(View Logs)"
                      styleClass="tw:ms-1 tw:py-0" />
                  }
                </div>
              </div>

              <div class="tw:flex tw:flex-row">
                <div class="col-4">
                  <label>Duration</label>
                  <cor-job-runtime-info
                    [jobSteps]="jobData().jobSteps"
                    [useLink]="true"
                    (click)="$event.stopPropagation()" />
                </div>
                <div class="col-8 tw:flex tw:gap-2">
                  <label>
                    {{
                      `Compile Time: ${
                        jobData().compileTime ? (jobData().compileTime | timeTaken) : 'N/A'
                      }, Queue Time: ${
                        jobData().queueTime ? (jobData().queueTime | timeTaken) : 'N/A'
                      }, Run Time:
                    ${jobData().runTime ? (jobData().runTime | timeTaken) : 'N/A'}`
                    }}
                  </label>
                </div>
              </div>

              <div class="tw:flex tw:flex-row">
                <div class="col-4">
                  <label>Creation Date</label>
                </div>
                <div class="col-8">
                  <label>{{ jobData()?.createdDate | localDate }}</label>
                </div>
              </div>
            </div>
          }
        </p-card>
      </section>
    }

    <!-- Description Section -->
    <p-card [style]="{ 'margin-bottom': '0.75rem' }">
      <section id="description">
        <!-- Job Creation Form starts -->
        <!-- Description Form Group -->
        <div class="row">
          <div class="col-12">
            <div class="row">
              <!-- Description -->
              <cor-input-container
                class="col-12"
                label="Description"
                test-id="description">
                <cor-description
                  test-id="run-job-details-description"
                  [minHeight]="70"
                  placeholder="Here you can put a brief description of the job"
                  [formControl]="form.controls.descriptionFormGroup.controls.description" />
              </cor-input-container>
            </div>
          </div>

          @if (form.controls.executionType.value === EXECUTION_TYPE.MONITORING) {
            <div class="row">
              <!-- Submission Date Section - Only Supported in Monitoring Job -->
              <cor-input-container
                class="col-4"
                label="Submission Date"
                helperText="The date to tag the metrics from this job to - shown in Monitoring Overview"
                test-id="submission-date">
                <p-datepicker
                  test-id="run-job-details-submission-date"
                  placeholder="Select Submission Date for the Monitoring Task"
                  [formControl]="form.controls.descriptionFormGroup.controls.submissionDate"
                  dateFormat="mm-dd-yy"
                  [showClear]="true"
                  [showIcon]="true"
                  iconDisplay="input"
                  appendTo="body" />
                <cor-field-error
                  [visible]="
                    form.controls.descriptionFormGroup.controls.submissionDate.touched &&
                    form.controls.descriptionFormGroup.controls.submissionDate.errors?.invalidDate
                  "
                  errorMsg="Submission Date is not valid" />
              </cor-input-container>
            </div>
          }

          @if ([VIEW_TYPE.NEW, VIEW_TYPE.RERUN] | includes: viewType()) {
            <p-button
              test-id="run-job-details-show-additional-options"
              [buttonProps]="{ autofocus: false }"
              (onClick)="toggleSignal(showAdditionalOptions)"
              variant="text"
              severity="primary"
              [label]="
                showAdditionalOptions() ? 'Hide Additional Options' : 'Show Additional Options'
              "
              styleClass="tw:py-2" />
          }
          @if (
            !([VIEW_TYPE.NEW, VIEW_TYPE.RERUN] | includes: viewType()) || showAdditionalOptions()
          ) {
            <div class="row">
              <cor-input-container
                label="Execution Concurrency"
                tooltipText="Number of threads used to process records at the same time. Higher values can speed up execution but require more system resources"
                class="col-4">
                <p-inputnumber
                  test-id="run-job-details-parallelism-count"
                  placeholder="Number of parallel jobs"
                  [showButtons]="true"
                  [min]="1"
                  [max]="8"
                  [formControl]="form.controls.descriptionFormGroup.controls.parallelismCount" />
              </cor-input-container>
              <cor-input-container
                label="Execution Timeout"
                tooltipText="Maximum number of hours the job is allowed to run before it is automatically terminated. Leave empty to use default value configured by your admin"
                class="col-4">
                <p-inputnumber
                  test-id="run-job-details-execution-timeout-hours"
                  placeholder="Time Limit (in hours)"
                  [showButtons]="true"
                  [min]="1"
                  [formControl]="
                    form.controls.descriptionFormGroup.controls.executionTimeoutHours
                  " />
              </cor-input-container>
            </div>
          }
        </div>
      </section>
    </p-card>

    <!-- Show Prompts for Execution Type = Prompt Simulation, we are currently showing for Pipeline-->
    @if (
      form.controls.executionType.value === EXECUTION_TYPE.HILL_CLIMBING &&
      viewType() !== VIEW_TYPE.RERUN_REPORTS
    ) {
      <!-- NOTE: hillclimbing-prompt class is used by parent component to hide the prompt section in Hillclimb+comparison view -->
      <p-panel
        [toggleable]="true"
        styleClass="tw:mb-2 hillclimbing-prompt"
        iconPos="start"
        toggler="header">
        <!-- Start: header icon -->
        <ng-template
          #headericons
          let-collapsed>
          <i
            class="pi"
            [ngClass]="{
              'pi-chevron-down': !collapsed,
              'pi-chevron-right': collapsed,
            }"></i>
        </ng-template>
        <!-- End: header icon -->

        <ng-template #header>
          <div class="tw:whitespace-nowrap tw:text-xl tw:w-full">Prompts</div>
        </ng-template>

        <!-- Start: content -->
        <div
          class="tw:m-3"
          id="prompts">
          @if (loadingState.promptLoading()) {
            <cor-skeleton-loader
              [count]="2"
              height="3rem"
              margin="0 0 3px 0" />
          } @else {
            @if (hillClimbing.promptList().length === 0) {
              @if (viewType() === VIEW_TYPE.EXISTING) {
                <span class="tw:text-muted-color">No Prompts were modified</span>
              } @else {
                <p-message
                  severity="error"
                  variant="simple">
                  Hill Climbing not supported as no prompts were found for the current pipeline
                </p-message>
              }
            } @else {
              @for (
                promptFormGroup of form.controls.hillClimbingPromptAssets.controls;
                let promptIndex = $index;
                track promptIndex
              ) {
                <div class="tw:mb-2">
                  <p-panel
                    [toggleable]="true"
                    iconPos="start"
                    toggler="header"
                    [attr.test-id]="
                      'hillclimb-prompt-' + promptFormGroup.controls.entityName.value
                    ">
                    <!-- Start: header -->
                    <ng-template #header>
                      <div class="tw:flex tw:justify-between tw:items-center tw:w-full tw:relative">
                        <div class="tw:flex tw:grow tw:items-center tw:justify-between">
                          <h5 class="tw:text-xl tw:mb-0 tw:ps-2">
                            Prompt {{ promptIndex + 1 }} -
                            {{ promptFormGroup.controls.entityName.value }}
                          </h5>
                          <div class="tw:flex tw:gap-2 tw:items-center">
                            @if (viewType() !== VIEW_TYPE.EXISTING) {
                              <p-button
                                test-id="run-job-details-reset-prompt-template-to-the-currently-r"
                                [buttonProps]="{ autofocus: false }"
                                pTooltip="Reset prompt template to the currently registered value"
                                tooltipPosition="left"
                                (click)="
                                  resetPrompt(
                                    promptFormGroup.controls.entityId.value,
                                    promptIndex,
                                    $event
                                  )
                                "
                                severity="secondary"
                                variant="text"
                                icon="pi pi-sync" />
                            }
                            <p-button
                              test-id="run-job-details-open-this-prompt-in-a-new-tab"
                              [buttonProps]="{ autofocus: false }"
                              pTooltip="Open this prompt in a new tab"
                              tooltipPosition="bottom"
                              (click)="
                                openPromptInNewTab(promptFormGroup.controls.entityId.value, $event)
                              "
                              variant="text"
                              icon="pi pi-external-link" />
                            @if (viewType() !== VIEW_TYPE.EXISTING) {
                              <p-button
                                test-id="run-job-details-remove-this-prompt"
                                [buttonProps]="{ autofocus: false }"
                                pTooltip="Remove this prompt"
                                tooltipPosition="bottom"
                                (click)="
                                  removeHillClimbingPrompt(promptFormGroup.controls.entityId.value)
                                "
                                variant="text"
                                icon="pi pi-trash"
                                severity="danger" />
                            }
                          </div>
                        </div>
                      </div>
                    </ng-template>
                    <!-- End: header -->

                    <!-- Start: header icons -->
                    <ng-template
                      #headericons
                      let-collapsed>
                      <i
                        class="pi"
                        [ngClass]="{
                          'pi-chevron-down': !collapsed,
                          'pi-chevron-right': collapsed,
                        }"></i>
                    </ng-template>
                    <!-- End: header icons -->

                    <div>
                      <div class="tw:flex tw:flex-col">
                        <ng-container *ngTemplateOutlet="promptDefTemplate" />
                      </div>
                    </div>
                  </p-panel>
                </div>

                <ng-template #promptDefTemplate>
                  @if (viewType() === VIEW_TYPE.EXISTING) {
                    @defer (on viewport) {
                      <cor-diff-viewer
                        [leftValue]="
                          hillClimbing.promptDefinitionStore()[
                            promptFormGroup.controls.entityId.value
                          ]
                        "
                        [rightValue]="promptFormGroup.controls.promptTemplate.value"
                        language="plaintext" />
                    } @placeholder {
                      <cor-skeleton-loader height="13rem" />
                    }
                  } @else if (viewType() === VIEW_TYPE.NEW) {
                    <cor-prompt-definition
                      [attr.test-id]="
                        'hillclimb-prompt-definition-' + promptFormGroup.controls.entityName.value
                      "
                      location="job-details"
                      [baselineValue]="
                        hillClimbing.promptDefinitionStore()[
                          promptFormGroup.controls.entityId.value
                        ]
                      "
                      [formControl]="promptFormGroup.controls.promptTemplate" />
                  } @else {
                    <!-- Re-run -->
                    <cor-prompt-definition
                      [attr.test-id]="
                        'hillclimb-prompt-definition-' + promptFormGroup.controls.entityName.value
                      "
                      location="job-details"
                      [baselineValue]="
                        hillClimbing.promptDefinitionStore()[
                          promptFormGroup.controls.entityId.value
                        ]
                      "
                      [formControl]="promptFormGroup.controls.promptTemplate" />
                  }
                </ng-template>
              }

              @if (viewType() !== VIEW_TYPE.EXISTING) {
                <p-button
                  test-id="hillclimb-add-prompt-button"
                  [buttonProps]="{ autofocus: false }"
                  variant="outlined"
                  size="small"
                  icon="pi pi-plus"
                  label="Add prompt"
                  (click)="promptListPopoverElement.toggle($event)" />

                <p-popover #promptListPopoverElement>
                  <div class="tw:flex tw:flex-col">
                    @for (prompt of hillClimbing.promptList(); track prompt.id) {
                      @let disabled =
                        form.controls.hillClimbingPromptAssets.getRawValue()
                          | findItem: ['entityId', prompt.id];
                      <p-button
                        [attr.test-id]="'hillclimb-add-prompt-option-' + prompt.name"
                        class="tw:justify-start"
                        [style]="{ width: '100%', 'justify-content': 'start' }"
                        icon="pi pi-align-left"
                        [buttonProps]="{ autofocus: false }"
                        [label]="prompt.name"
                        severity="contrast"
                        variant="text"
                        [disabled]="disabled"
                        [pTooltip]="disabled ? 'Already Added' : ''"
                        position="left"
                        (click)="
                          addHillClimbingPrompt(prompt); promptListPopoverElement.toggle($event)
                        " />
                    }
                  </div>
                </p-popover>
              }
            }
          }
        </div>
        <!-- End: content -->
      </p-panel>
    }

    <!-- Scheduling Section - Only for Monitoring jobs -->
    @if (
      form.controls.executionType.value === EXECUTION_TYPE.MONITORING &&
      viewType() !== VIEW_TYPE.RERUN_REPORTS
    ) {
      <p-panel
        [toggleable]="true"
        iconPos="start"
        [collapsed]="true"
        toggler="header"
        test-id="scheduling-accordion">
        <!-- Start: header -->
        <ng-template #header>
          <div class="p-panel-title tw:ms-2 tw:w-full">Scheduling</div>
        </ng-template>

        <!-- Start: header icons -->
        <ng-template
          #headericons
          let-collapsed>
          <i
            class="pi"
            [ngClass]="{
              'pi-chevron-down': !collapsed,
              'pi-chevron-right': collapsed,
            }"></i>
        </ng-template>
        <!-- End: header icons -->
        <!-- Start: content -->
        <div class="tw:mt-3">
          <div class="row">
            <cor-input-container
              class="col-12 col-sm-6 col-md-4 tw:max-w-fit"
              label="Start Date"
              test-id="scheduled-date">
              <p-datepicker
                test-id="run-job-details-scheduled-day"
                [placeholder]="form.enabled ? 'Today' : ''"
                [showClear]="true"
                [showIcon]="true"
                iconDisplay="input"
                [inputStyle]="{ width: '200px' }"
                [formControl]="form.controls.schedulingFormGroup.controls.scheduledDay"
                dateFormat="mm/dd/yy"
                appendTo="body" />
              <cor-field-error
                [visible]="
                  (form.controls.schedulingFormGroup.controls.scheduledDay.touched ||
                    form.controls.schedulingFormGroup.controls.scheduledTime.touched) &&
                  form.controls.schedulingFormGroup.errors?.invalidDate
                "
                errorMsg="Scheduled Time is not valid" />
            </cor-input-container>
            <cor-input-container
              class="col-12 col-sm-6 col-md-4 tw:max-w-fit"
              label="Time"
              test-id="scheduled-date">
              <p-datepicker
                test-id="run-job-details-scheduled-time"
                [placeholder]="form.enabled ? 'Run Immediately' : ''"
                [showClear]="true"
                [timeOnly]="true"
                [stepMinute]="30"
                [inputStyle]="{ width: '200px' }"
                dataType="string"
                hourFormat="24"
                [formControl]="form.controls.schedulingFormGroup.controls.scheduledTime"
                appendTo="body" />
            </cor-input-container>
            <cor-input-container
              class="col-12 col-sm-6 col-md-4"
              label="Recurrence">
              <p-select
                [options]="recurrenceOptions()"
                optionLabel="text"
                optionValue="value"
                [formControl]="form.controls.schedulingFormGroup.controls.frequency"
                test-id="recurrence-dropdown"
                [filter]="true"
                [showClear]="true" />
            </cor-input-container>
          </div>
          <!-- do not show recurrence dates for job dashboard ie. when viewing existing job -->
          @if (viewType() !== VIEW_TYPE.EXISTING && recurrenceDates().length > 0) {
            <div class="row tw:mt-2">
              <div class="col-12 recurrences">
                {{ `First ${recurrenceDates().length} iterations will run on:` }}
                <span class="tw:italic">
                  {{ recurrenceDates() | join: { delimiter: ', ', quotes: false } }}
                </span>
              </div>
            </div>
          }
        </div>
        <!-- End: content -->
      </p-panel>
    }

    <!-- Inputs aka Dependencies Section - Don't show for Simulation and HillClimbing -->
    @if (
      !(
        [EXECUTION_TYPE.SIMULATION, EXECUTION_TYPE.HILL_CLIMBING, EXECUTION_TYPE.MONITORING]
        | includes: form.controls.executionType.value
      )
    ) {
      <p-panel
        header="Dependencies"
        [toggleable]="true"
        toggler="header">
        <!-- Start: header icon -->
        <ng-template
          #headericons
          let-collapsed>
          <i
            class="pi"
            [ngClass]="{
              'pi-chevron-down': !collapsed,
              'pi-chevron-right': collapsed,
            }"></i>
        </ng-template>
        <!-- End: header icon -->

        <!-- Start: content -->
        <!-- Inputs Form Group -->
        <div
          class="tw:mt-2"
          id="inputs">
          <!-- Current Object starts -->
          <ng-container>
            <div class="row">
              <div class="col-12">
                <label>Current Object:</label>
                @if (loadingState.isCurrentObjectDataLoading()) {
                  <strong class="tw:text-muted-color tw:ms-1">Loading ...</strong>
                } @else {
                  <strong>
                    {{
                      form.controls.inputsFormGroup.controls.currentObjectFormGroup.controls
                        .currentObjectName.value
                    }}
                  </strong>
                }
              </div>
            </div>
          </ng-container>
          <!-- Current Object ends -->
        </div>
        <!-- End: content -->
      </p-panel>
    }

    <!-- For Model, Prompt, RAG, Pipeline, Monitoring - we ask for the Evaluations the user wants to run -->
    @if (form.controls.dataLogicFormArray) {
      <div class="tw:my-2">
        @for (
          dataLogicFormGroup of form.controls.dataLogicFormArray.controls;
          track dataLogicFormGroup;
          let dataLogicIndex = $index
        ) {
          <p-panel
            [toggleable]="true"
            styleClass="tw:mb-2"
            iconPos="start"
            toggler="header">
            <!-- Start: header -->
            <ng-template #header>
              <div
                class="tw:flex tw:px-2 tw:flex-auto tw:gap-2 tw:justify-between tw:items-center"
                [attr.test-id]="'eval-header-' + dataLogicIndex">
                <div class="tw:whitespace-nowrap tw:text-xl">
                  {{ 'Evaluation #' + (dataLogicIndex + 1) }}
                </div>
                <div class="tw:flex tw:flex-auto tw:items-center tw:justify-end tw:gap-3">
                  @if (
                    viewType() !== VIEW_TYPE.EXISTING ||
                    dataLogicFormGroup.controls.reportVersionId.value
                  ) {
                    <cor-input-select
                      [disabled]="object() === OBJECTS.REPORT"
                      [ngClass]="{
                        'col col-md-8':
                          !dataLogicFormGroup.controls.reportVersionId.disabled &&
                          object() !== OBJECTS.REPORT,
                      }"
                      (click)="
                        !dataLogicFormGroup.controls.reportVersionId.disabled &&
                          object() !== OBJECTS.REPORT &&
                          onReportSelectionClick($event, dataLogicIndex)
                      "
                      (mousedown)="
                        !dataLogicFormGroup.controls.reportVersionId.disabled &&
                          object() !== OBJECTS.REPORT &&
                          $event.stopPropagation()
                      "
                      (keydown)="
                        !dataLogicFormGroup.controls.reportVersionId.disabled &&
                          object() !== OBJECTS.REPORT &&
                          $event.stopPropagation()
                      "
                      [inputList]="inputDataSources().reportList"
                      [filterByProject]="false"
                      placeholder="Select a report to run"
                      [searchKeys]="['alias', 'name']"
                      valueExpr="id"
                      displayExpr="name"
                      [formControl]="dataLogicFormGroup.controls.reportVersionId"
                      test-id="report-selection"
                      mode="single"
                      [openOnFieldClick]="false"
                      [showDropDownButton]="false" />
                  }
                  @if (
                    !([VIEW_TYPE.EXISTING, VIEW_TYPE.RERUN_REPORTS] | includes: viewType()) &&
                    form.controls.dataLogicFormArray.length > 1
                  ) {
                    <i
                      pTooltip="Delete"
                      tooltipPosition="bottom"
                      [attr.test-id]="'eval-delete-' + dataLogicIndex"
                      (click)="dataLogicDelete(dataLogicIndex)"
                      aria-hidden="true"
                      class="pi pi-trash tw:float-right tw:cursor-pointer"></i>
                  }
                </div>
              </div>
            </ng-template>
            <!-- End: header -->

            <!-- Start: header icon -->
            <ng-template
              #headericons
              let-collapsed>
              <i
                class="pi"
                [ngClass]="{
                  'pi-chevron-down': !collapsed,
                  'pi-chevron-right': collapsed,
                }"></i>
            </ng-template>
            <!-- End: header icon -->

            <!-- Start: content -->
            @if (object() === OBJECTS.PIPELINE && pipelineType() === PIPELINE_TYPE.CHAT) {
              <section
                id="simulation-type"
                class="tw:px-3 tw:pt-3">
                <cor-input-container
                  [label]="'Simulation Type'"
                  [attr.test-id]="'simulation-type-' + dataLogicIndex">
                  <div class="tw:grid tw:md:grid-cols-2 tw:gap-3 tw:mt-1">
                    <label
                      class="tw:flex tw:cursor-pointer tw:items-start tw:gap-3 tw:rounded-xl tw:border tw:border-surface tw:p-3"
                      [ngClass]="{
                        'tw:bg-highlight tw:border-primary!':
                          dataLogicFormGroup.controls.evaluationType.value ===
                          EVALUATION_TYPE.SINGLE_TURN,
                      }">
                      <p-radiobutton
                        test-id="run-job-details-evaluation-type"
                        [name]="'evaluationType-' + dataLogicIndex"
                        [value]="EVALUATION_TYPE.SINGLE_TURN"
                        class="tw:my-1"
                        [formControl]="dataLogicFormGroup.controls.evaluationType" />
                      <div class="tw:flex tw:flex-col tw:gap-1">
                        <div
                          class="tw:flex tw:items-center tw:gap-3 tw:text-lg tw:font-medium tw:text-color">
                          <i class="pi pi-arrow-right-arrow-left"></i>
                          {{ 'Single Turn' }}
                        </div>
                        <div class="tw:text-sm tw:text-muted-color">
                          {{ 'You provide one input object per row.' }}
                        </div>
                      </div>
                    </label>

                    <label
                      class="tw:flex tw:cursor-pointer tw:items-start tw:gap-3 tw:rounded-xl tw:border tw:border-surface tw:p-3"
                      [ngClass]="{
                        'tw:bg-highlight tw:border-primary!':
                          dataLogicFormGroup.controls.evaluationType.value ===
                          EVALUATION_TYPE.MULTI_TURN,
                      }">
                      <p-radiobutton
                        test-id="run-job-details-evaluation-type-2"
                        [name]="'evaluationType-' + dataLogicIndex"
                        [value]="EVALUATION_TYPE.MULTI_TURN"
                        class="tw:my-1"
                        [formControl]="dataLogicFormGroup.controls.evaluationType" />
                      <div class="tw:flex tw:flex-1 tw:flex-col tw:gap-1">
                        <div
                          class="tw:flex tw:items-center tw:gap-2 tw:text-lg tw:font-medium tw:text-color">
                          <i class="pi pi-comments"></i>
                          {{ 'Multi Turn' }}
                        </div>
                        <div class="tw:text-sm tw:text-muted-color">
                          {{ 'A tester agent holds a full conversation with the pipeline.' }}
                        </div>
                      </div>
                    </label>
                  </div>
                </cor-input-container>

                @if (
                  dataLogicFormGroup.controls.evaluationType.value === EVALUATION_TYPE.MULTI_TURN
                ) {
                  <p-message
                    severity="secondary"
                    styleClass="tw:mt-3 tw:w-full">
                    <div class="tw:flex tw:flex-col tw:gap-2 tw:py-1">
                      <span class="tw:font-semibold tw:text-color">
                        Set up a conversation-based evaluation
                      </span>
                      <ol
                        class="tw:mb-0 tw:list-decimal tw:space-y-1 tw:ps-5 tw:text-sm tw:text-color-secondary">
                        <li>
                          Choose a data source with one test case per row. Include
                          <code>context</code>
                          for the pipeline's starting state and
                          <code>scenario</code>
                          for the tester's objective.
                        </li>
                        <li>
                          Optionally include
                          <code>reference_conversation</code>
                          when the tester should follow an existing example conversation.
                        </li>
                        <li>
                          Configure the model that acts as the tester, then describe the user it
                          should impersonate.
                        </li>
                      </ol>
                    </div>
                  </p-message>
                }
              </section>
            }

            @if (
              dataLogicFormGroup.controls.reportParameters &&
              dataLogicFormGroup.controls.reportParameters.controls.length > 0
            ) {
              <div
                class="tw:border-b tw:border-surface tw:p-3"
                [attr.test-id]="'eval-params-' + dataLogicIndex">
                <div class="tw:flex tw:items-center tw:mb-2 tw:gap-4">
                  <span class="group-heading tw:text-color">Parameters</span>
                  @if (
                    dataLogicState()[dataLogicIndex].hasOptionalParameters() &&
                    !dataLogicState()[dataLogicIndex].reportParametersListLoading()
                  ) {
                    <p-button
                      test-id="run-job-details-show-additional-report-parameters"
                      [buttonProps]="{ autofocus: false }"
                      (click)="
                        toggleSignal(dataLogicState()[dataLogicIndex].showOptionalParameters)
                      "
                      variant="text"
                      size="small"
                      [label]="
                        dataLogicState()[dataLogicIndex].showOptionalParameters()
                          ? 'Hide additional report parameters'
                          : 'Show additional report parameters'
                      "
                      styleClass="tw:py-0" />
                  }
                </div>

                @if (dataLogicState()[dataLogicIndex].reportParametersListLoading()) {
                  <cor-skeleton [type]="SKELETON_TYPES.LIST" />
                } @else {
                  <div class="row">
                    <ng-container
                      [ngTemplateOutlet]="reportParameterTemplate"
                      [ngTemplateOutletContext]="{
                        reportParametersFormArray:
                          dataLogicFormGroup.controls.reportParameters.controls,
                        parameterFields: dataLogicState()[dataLogicIndex].parameterFields(),
                        showOptionalParameters:
                          dataLogicState()[dataLogicIndex].showOptionalParameters(),
                      }" />
                  </div>
                }
              </div>
            }
            @if (
              dataLogicFormGroup.controls.metricThresholds &&
              dataLogicFormGroup.controls.metricThresholds.controls.length > 0
            ) {
              <div
                class="tw:border-b tw:border-surface tw:p-3"
                test-id="eval-metrics">
                <div class="col-12 tw:mt-3">
                  <span class="group-heading tw:text-color">Metric Thresholds</span>

                  @if (dataLogicState()[dataLogicIndex].metricThresholdsListLoading()) {
                    <cor-skeleton [type]="SKELETON_TYPES.LIST" />
                  } @else {
                    @for (
                      metricThreshold of dataLogicFormGroup.controls.metricThresholds.controls;
                      track metricThreshold;
                      let currentIndex = $index
                    ) {
                      <ng-container test-id="metric-threshold-form">
                        <div class="row">
                          <div class="col-4">
                            <span class="cluster-heading tw:text-muted-color">
                              {{ metricThreshold.controls.label.value }}
                            </span>
                          </div>
                          <div class="col-3">
                            <cor-input-container test-id="threshold-lower">
                              <p-inputnumber
                                test-id="run-job-details-threshold-lower"
                                placeholder="Type lower threshold"
                                [formControl]="metricThreshold.controls.thresholdLower" />
                            </cor-input-container>
                          </div>
                          <div class="col-3">
                            <cor-input-container test-id="threshold-upper">
                              <p-inputnumber
                                test-id="run-job-details-threshold-upper"
                                placeholder="Type upper threshold"
                                [formControl]="metricThreshold.controls.thresholdUpper" />
                              <cor-field-error
                                [visible]="
                                  metricThreshold.invalid &&
                                  metricThreshold.errors?.thresholdRangeError
                                "
                                errorMsg="Upper Threshold should be greater than Lower Threshold" />
                            </cor-input-container>
                          </div>
                        </div>
                      </ng-container>
                    }
                  }
                </div>
              </div>
            }
            <div
              class="tw:p-3 tw:flex tw:flex-col tw:gap-2"
              [attr.test-id]="'eval-data' + dataLogicIndex">
              <span class="group-heading tw:text-color">Data Source</span>

              <!-- Example data source option for selected reports -->
              @let reportDataSources =
                [
                  ...(dataLogicState()[dataLogicIndex]?.reportDetails()?.dataLogicExamples || []),
                  ...defaultDataSources(),
                ];
              @if (viewType() === VIEW_TYPE.EXISTING) {
                <!-- Show nothing here as the user does not need to select the data, just viewing -->
              } @else if (viewType() === VIEW_TYPE.RERUN_REPORTS) {
                <small class="tw:text-muted-color">
                  Existing report data will be used. You can't modify the data while rerunning
                  reports
                </small>
              } @else if (reportDataSources?.length > 1) {
                <!-- Report was selected and it has examples -->
                <small class="tw:text-muted-color">
                  Use an existing data source provided for this report - or create your own custom
                  data source for this evaluation
                </small>

                <div
                  class="tw:grid tw:grid-cols-3 tw:gap-2"
                  test-id="eval-data-options">
                  @for (itemData of reportDataSources; track itemData.id) {
                    <label
                      class="tw:col-span-1 tw:flex tw:items-start tw:gap-3 tw:rounded-xl tw:border tw:border-surface tw:p-3"
                      [attr.test-id]="'eval-data-options-item-' + itemData.name"
                      [ngClass]="{
                        'tw:border-primary! tw:bg-highlight':
                          itemData.id ===
                          dataLogicState()[dataLogicIndex].selectedReportDataSource()?.id,
                        'tw:cursor-pointer': !itemData.disabled,
                        'tw:opacity-75': itemData.disabled,
                      }"
                      [pTooltip]="
                        itemData.disabled && itemData.id === CUSTOM_DATA_DATASOURCE.id
                          ? 'You do not have permission to upload custom data'
                          : ''
                      ">
                      <p-radiobutton
                        test-id="run-job-details-change-report-data-source"
                        [ngModel]="dataLogicState()[dataLogicIndex].selectedReportDataSource()?.id"
                        [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="changeReportDataSource(dataLogicFormGroup, $event)"
                        [name]="'reportDataSource-' + dataLogicIndex"
                        [inputId]="'ds-' + dataLogicIndex + '-' + itemData.id"
                        [value]="itemData.id"
                        [disabled]="itemData.disabled"
                        class="tw:my-1" />

                      <div class="tw:flex tw:flex-1 tw:flex-col tw:gap-1">
                        <div
                          class="tw:flex tw:items-center tw:gap-2 tw:text-base tw:font-medium tw:text-color tw:break-words">
                          <i [class]="`pi ${itemData.icon || 'pi-file'}`"></i>
                          {{ itemData.name }}
                        </div>
                        <div class="tw:text-sm tw:text-muted-color tw:truncate-2">
                          {{ itemData.description || '' }}
                        </div>
                      </div>
                    </label>
                  }
                </div>
              } @else {
                <small class="tw:text-muted-color">Provide data to use for this evaluation</small>
              }
              @if (
                dataLogicState()[dataLogicIndex].selectedReportDataSource() &&
                viewType() !== VIEW_TYPE.RERUN_REPORTS
              ) {
                @if (viewType() !== VIEW_TYPE.EXISTING) {
                  <div
                    class="col-12 tw:my-1"
                    test-id="expected-columns">
                    <span style="font-size: 0.9em">
                      @if (object() === OBJECTS.REPORT) {
                        The provided data should contain columns from the object being run AND the
                        following columns for the report:
                      } @else {
                        The provided data should contain the following columns:
                      }
                    </span>
                    @for (
                      arg of dataLogicState()[dataLogicIndex].expectedArguments();
                      track arg.alias
                    ) {
                      <p-tag
                        severity="success"
                        [value]="arg.alias + ': ' + (arg.type | truncate: 100)"
                        class="tw:me-2 tw:py-0 tw:font-medium"
                        [style]="arg.isMandatory ? '' : 'opacity: 70%'"
                        [pTooltip]="
                          (arg.isMandatory ? '' : 'Optional ') +
                          'Argument from ' +
                          (arg.objects | join: { delimiter: ', ', quotes: true }) +
                          (arg.type.length > 100 ? ' with type ' + arg.type : '')
                        " />
                    }

                    <!-- In case of selected reports, show their report column -->
                    @for (
                      param of dataLogicState()[dataLogicIndex].parameterColumns();
                      track param
                    ) {
                      <p-tag
                        severity="warn"
                        [value]="param.alias + ': str'"
                        class="tw:me-2 tw:py-0 tw:font-medium"
                        [style]="param.isMandatory ? '' : 'opacity: 70%'"
                        [pTooltip]="
                          (param.isMandatory ? '' : 'Optional ') +
                          'Report Column from ' +
                          (param.source | join: { delimiter: ', ', quotes: true }) +
                          (param.description ? `\n ${param.description}` : '')
                        " />
                    }

                    <!-- AI Data Generation Button -->
                    @if (
                      viewType() !== VIEW_TYPE.EXISTING &&
                      dataLogicState()[dataLogicIndex].selectedReportDataSource()?.id ===
                        AI_GENERATED_DATASOURCE.id
                    ) {
                      <div class="col-12 tw:mt-2">
                        <p-button
                          test-id="run-job-details-regenerate-data-with-ai"
                          [buttonProps]="{ autofocus: false }"
                          (click)="onDataGenerationClick(dataLogicIndex)"
                          pTooltip="Regenerate synthetic data for this evaluation using AI"
                          [disabled]="isGeneratingAiData()"
                          [loading]="isGeneratingAiData()"
                          icon="pi pi-sparkles"
                          size="small"
                          [label]="
                            isGeneratingAiData() ? 'Generating...' : 'Regenerate Data with AI'
                          " />
                      </div>
                    }
                  </div>
                }

                @if (
                  dataLogicFormGroup.controls.inputType.value === MODEL_INPUT_TYPE.PYTHON_FUNCTION
                ) {
                  <!-- Show Input Tables for registered tables data source (but not if annotation queues are present) -->
                  @if (
                    (viewType() !== VIEW_TYPE.EXISTING &&
                      dataLogicState()[dataLogicIndex].selectedReportDataSource()?.id !==
                        ANNOTATION_QUEUE_DATASOURCE.id) ||
                    (viewType() === VIEW_TYPE.EXISTING &&
                      dataLogicFormGroup.controls.tableIds.value.length > 0)
                  ) {
                    <div class="row">
                      <cor-input-container
                        label="Input Tables"
                        test-id="input-tables"
                        class="col">
                        <cor-input-select
                          test-id="run-job-details-table-ids"
                          placeholder="Select Tables"
                          [inputList]="dataLogicInfo.inputTablesList()"
                          [formControl]="dataLogicFormGroup.controls.tableIds" />
                      </cor-input-container>
                      @if (
                        viewType() !== VIEW_TYPE.EXISTING &&
                        dataLogicFormGroup.controls.tableIds.value.length > 0 &&
                        dataLogicFormGroup.controls.inputFeatureVersionIds.value.length === 0
                      ) {
                        <p-button
                          test-id="run-job-details-add-additional-resources"
                          [buttonProps]="{ autofocus: false }"
                          (click)="toggleResourceInput(dataLogicState()[dataLogicIndex])"
                          styleClass="tw:px-1 tw:py-0"
                          size="small"
                          variant="text"
                          [icon]="
                            dataLogicState()[dataLogicIndex].showResourceInput()
                              ? 'pi pi-minus'
                              : 'pi pi-plus'
                          "
                          [label]="
                            dataLogicState()[dataLogicIndex].showResourceInput()
                              ? 'Hide Additional Resources'
                              : 'Add Additional Resources'
                          " />
                      }
                    </div>
                  }

                  <!-- Show Annotation Queue selection for annotation queue data source OR if annotationQueueId has values -->
                  @else if (
                    (viewType() !== VIEW_TYPE.EXISTING &&
                      dataLogicState()[dataLogicIndex].selectedReportDataSource()?.id ===
                        ANNOTATION_QUEUE_DATASOURCE.id) ||
                    (viewType() === VIEW_TYPE.EXISTING &&
                      dataLogicFormGroup.controls.annotationQueueId.value)
                  ) {
                    <div class="row">
                      <cor-input-container
                        label="Annotation Queue"
                        test-id="annotation-queue"
                        class="col-12 col-md-6">
                        <cor-input-select
                          test-id="run-job-details-annotation-queue-id"
                          mode="single"
                          placeholder="Select an annotation queue for data input"
                          [inputList]="dataLogicInfo.inputAnnotationQueuesList()"
                          [formControl]="dataLogicFormGroup.controls.annotationQueueId"
                          valueExpr="id"
                          displayExpr="name" />
                      </cor-input-container>

                      <!-- Date Range Input for Annotation Queue Filtering -->
                      @if (dataLogicFormGroup.controls.annotationQueueId.value) {
                        <cor-input-container
                          label="Date Range"
                          test-id="aq-date-range"
                          class="col-12 col-md-4">
                          <p-datepicker
                            test-id="run-job-details-aq-date-range"
                            [selectOtherMonths]="true"
                            iconDisplay="input"
                            [style]="{ width: '250px' }"
                            position="left"
                            selectionMode="range"
                            [formControl]="dataLogicFormGroup.controls.aqDateRange"
                            [showClear]="true"
                            placeholder="No date filters applied"
                            appendTo="body"
                            [showIcon]="true"
                            [numberOfMonths]="1"
                            dateFormat="mm/dd/yy"
                            rangeSeparator="to" />
                          <cor-field-error
                            [visible]="
                              dataLogicFormGroup.controls.aqDateRange.touched &&
                              dataLogicFormGroup.controls.aqDateRange.errors?.invalidDateRange
                            "
                            errorMsg="Please select both start and end dates" />
                        </cor-input-container>
                      }
                      @if (
                        viewType() !== VIEW_TYPE.EXISTING &&
                        dataLogicFormGroup.controls.annotationQueueId.value &&
                        dataLogicFormGroup.controls.inputFeatureVersionIds.value.length === 0
                      ) {
                        <p-button
                          test-id="run-job-details-add-additional-resources-2"
                          [buttonProps]="{ autofocus: false }"
                          (click)="toggleResourceInput(dataLogicState()[dataLogicIndex])"
                          styleClass="tw:px-1 tw:py-0"
                          variant="text"
                          size="small"
                          [icon]="
                            dataLogicState()[dataLogicIndex].showResourceInput()
                              ? 'pi pi-minus'
                              : 'pi pi-plus'
                          "
                          [label]="
                            dataLogicState()[dataLogicIndex].showResourceInput()
                              ? 'Hide Additional Resources'
                              : 'Add Additional Resources'
                          " />
                      }
                    </div>
                  }

                  <!-- For 'Custom Data' case - Show definition box after input tables are selected by the user -->
                  @if (
                    dataLogicFormGroup.controls.inputFeatureVersionIds.value.length > 0 ||
                    (viewType() !== VIEW_TYPE.EXISTING &&
                      dataLogicState()[dataLogicIndex].showResourceInput())
                  ) {
                    <cor-input-container
                      label="Resources &amp; Inputs"
                      test-id="resources"
                      class="col-12">
                      <cor-input-select
                        test-id="run-job-details-input-feature-version-ids"
                        placeholder="Select other objects like Prompts, Global Functions, etc. to prepare the data for this Evaluation"
                        [inputList]="dataLogicInfo.inputResourcesList()"
                        valueExpr="uniqueId"
                        [searchKeys]="['alias', 'name']"
                        [formControl]="dataLogicFormGroup.controls.inputFeatureVersionIds" />
                    </cor-input-container>
                  }
                }

                @if (dataLogicFormGroup.controls.inputType.value === MODEL_INPUT_TYPE.DATASOURCE) {
                  <cor-custom-hot-table
                    class="col-12"
                    [formControl]="dataLogicFormGroup.controls.dataFileCellValues"
                    [maxUploadSize]="maxUploadSize()" />
                } @else if (
                  dataLogicFormGroup.controls.tableIds.value.length > 0 ||
                  dataLogicFormGroup.controls.annotationQueueId.value
                ) {
                  <cor-definition
                    test-id="run-job-details-definition"
                    class="col-12"
                    [defnTemplate]="dataLogicFormGroup.controls.inputType.value"
                    [object]="object()"
                    [inputList]="dataLogicInfo.definitionInputs()"
                    [customSuggestions]="dataLogicState()[dataLogicIndex].customSuggestions()"
                    [location]="
                      dataLogicFormGroup.controls.annotationQueueId.value
                        ? 'job-aq-datasource'
                        : 'job-datasource'
                    "
                    [formControl]="dataLogicFormGroup.controls.definition" />
                  <cor-field-error
                    [visible]="
                      dataLogicFormGroup.controls.definition.invalid &&
                      dataLogicFormGroup.controls.definition.touched
                    "
                    [errorMsg]="
                      dataLogicFormGroup.controls.definition?.errors?.errorMessage ||
                      'Definition is required'
                    " />
                }
              }

              <!-- Multitun simulation tester llm config -->
              @if (
                (dataLogicFormGroup.controls.tableIds.value.length > 0 ||
                  dataLogicFormGroup.controls.annotationQueueId.value ||
                  dataLogicFormGroup.controls.inputType.value !==
                    MODEL_INPUT_TYPE.PYTHON_FUNCTION) &&
                dataLogicFormGroup.controls.evaluationType.value === EVALUATION_TYPE.MULTI_TURN
              ) {
                <p-panel styleClass="tw:mt-3">
                  <ng-template pTemplate="header">
                    <div class="tw:flex tw:flex-col tw:gap-0.5 tw:py-2">
                      <div class="tw:flex tw:items-center tw:gap-2">
                        <i class="pi pi-microchip-ai"></i>
                        <span class="tw:font-bold">Tester impersonator</span>
                      </div>
                      <span class="tw:text-sm tw:text-muted-color">
                        This model supplies each user message while your pipeline responds.
                      </span>
                    </div>
                  </ng-template>
                  <div class="tw:flex tw:flex-col tw:gap-4 tw:pt-3">
                    <div class="tw:flex tw:flex-col tw:gap-3">
                      <cor-json-editor
                        [label]="'Tester model configuration'"
                        helperText="The tester receives the conversation history after every turn. It stops when it reaches 'maxTurns' or when a configured 'stoppingFields' value is returned by the pipeline through the context."
                        [schemaConfig]="TESTER_LLM_CONFIG_SCHEMA"
                        [placeholder]="'Enter tester model configuration as JSON...'"
                        [formControl]="dataLogicFormGroup.controls.autoChatConfig" />
                      <cor-field-error
                        [visible]="
                          dataLogicFormGroup.controls.autoChatConfig.invalid &&
                          dataLogicFormGroup.controls.autoChatConfig.touched
                        "
                        [errorMsg]="'Tester model configuration is required'" />
                    </div>
                    <cor-input-container
                      [label]="'Tester impersonator instructions'"
                      [helperText]="'Describe the user, their goal, and how they should react. You can use {context}, {scenario}, and {reference_conversation}.'"
                      [attr.test-id]="'tester-instructions-' + dataLogicIndex">
                      <cor-code-mirror
                        [formControl]="dataLogicFormGroup.controls.autoChatInstructions"
                        [activeLanguage]="'plaintext'"
                        [placeholder]="'You are an impatient first-time user. Your goal is {scenario}. Use the current state, {context}, and ask one realistic follow-up question at a time.'" />
                    </cor-input-container>
                    <cor-field-error
                      [visible]="
                        dataLogicFormGroup.controls.autoChatInstructions.invalid &&
                        dataLogicFormGroup.controls.autoChatInstructions.touched
                      "
                      [errorMsg]="'Tester impersonator instructions are required'" />
                  </div>
                </p-panel>
              }
            </div>
            <!-- End: content -->
          </p-panel>
        }
      </div>
      @if (
        object() !== OBJECTS.REPORT &&
        !([VIEW_TYPE.EXISTING, VIEW_TYPE.RERUN_REPORTS] | includes: viewType())
      ) {
        <cor-add-more
          test-id="run-job-details-add-additional-evaluation"
          title="Add Additional Evaluation"
          (add)="addDataLogic()"
          type="rule" />
      }
    }
  </div>
}

<!-- Footer View Start -->
@if (!isExisitingJob() && form) {
  <!-- NOTE: tw:mb-4 was added to avoid overlap with HELP button -->
  <div class="tw:flex tw:justify-end tw:mt-2 tw:p-3 tw:gap-2 tw:mb-4">
    <!-- Show Run Job button -->
    @if (!jobId()) {
      <p-button
        [buttonProps]="{ autofocus: false }"
        icon="pi pi-sync"
        label="Run Job"
        test-id="run-job"
        [loading]="runningJob() === EVENT_TYPE.NEW"
        [disabled]="
          runningJob() ||
          loadingState.reportParametersLoading() ||
          loadingState.reportMetricsLoading()
        "
        severity="success"
        (click)="runJob(EVENT_TYPE.NEW)" />
    }

    <!-- Show Replace/New Simulation for Re-run -->
    @if (jobId()) {
      <!-- Cannot replace
         1. Approved jobs
         2. Jobs not created by current user
         3. Jobs for rerun reports (as base job is used for data)
         4. Hill climb jobs with a baseline -->
      @if (
        !(
          jobData()?.isVerified ||
          jobData()?.createdBy !== currentUser()?.name ||
          viewType() === VIEW_TYPE.RERUN_REPORTS ||
          baseHillclimbSimId()
        )
      ) {
        <p-button
          [buttonProps]="{ autofocus: false }"
          icon="pi pi-replay"
          label="Replace Job"
          [loading]="runningJob() === EVENT_TYPE.REPLACE"
          [disabled]="
            runningJob() ||
            loadingState.reportParametersLoading() ||
            loadingState.reportMetricsLoading()
          "
          severity="primary"
          test-id="replace-job"
          (click)="runJob(EVENT_TYPE.REPLACE)" />
      }
      <p-button
        [buttonProps]="{ autofocus: false }"
        icon="pi pi-sync"
        [label]="viewType() === VIEW_TYPE.RERUN_REPORTS ? 'Rerun Reports' : 'Run New Job'"
        [loading]="runningJob() === EVENT_TYPE.DEFAULT"
        [disabled]="
          runningJob() ||
          loadingState.reportParametersLoading() ||
          loadingState.reportMetricsLoading()
        "
        severity="success"
        test-id="run-new-job"
        (click)="
          viewType() === VIEW_TYPE.RERUN_REPORTS ? onReRunReports() : runJob(EVENT_TYPE.DEFAULT)
        " />
    }
  </div>
}
<!-- Footer View End -->

<!-- AI Data Generation Popup -->
<cor-dialog-box
  [(visible)]="aiDataGenerationModal.isOpen"
  title="Generate Data with AI"
  [style]="{ width: '70vw' }"
  (onHiding)="closeAiDataGenerationModal()">
  <div class="row">
    <p-message
      severity="warn"
      variant="simple"
      [motionOptions]="{ disabled: true }"
      class="tw:mb-2">
      {{
        `The data generation feature is driven by the definitions of Reports, Report Parameters, and the ${OBJECT_DISPLAY_NAMES[object()]} being executed.`
      }}
      <br />
      Ensure proper and detailed information is provided for each object description.
    </p-message>

    <!-- Report Description -->
    <div class="col-12 tw:mb-3">
      <cor-input-container
        label="Report Description"
        helperText="Describe the report so that AI can generate data that is relevant to it."
        test-id="report-description">
        <textarea
          test-id="run-job-details-report-description"
          pTextarea
          placeholder="Enter a description for the report to help AI generate data"
          [fluid]="true"
          [ngStyle]="{ 'min-height': '70px', 'field-sizing': 'content' }"
          rows="4"
          [formControl]="aiDataGenerationModal.form.controls.reportDescription"></textarea>
      </cor-input-container>
    </div>

    <!-- Object Description -->
    @if (object() !== OBJECTS.REPORT) {
      <div class="col-12 tw:mb-3">
        <cor-input-container
          [label]="`${OBJECT_DISPLAY_NAMES[object()]} Description`"
          helperText="Describe the object so that AI can generate data that is relevant to it."
          test-id="object-description">
          <textarea
            test-id="run-job-details-object-description"
            pTextarea
            placeholder="Enter a description for the to help AI generate data"
            [fluid]="true"
            [ngStyle]="{ 'min-height': '70px', 'field-sizing': 'content' }"
            rows="4"
            [formControl]="aiDataGenerationModal.form.controls.objectDescription"></textarea>
        </cor-input-container>
      </div>
    }

    <!-- Additional Context (Editable) -->
    <div class="col-12 tw:mb-3">
      <cor-input-container
        label="Additional Context"
        helperText="Provide any additional details to help AI generate more accurate data."
        test-id="additional-context">
        <textarea
          test-id="run-job-details-additional-context"
          pTextarea
          placeholder="Additional requirements to improve the quality of generate data, e.g. Limit to 10 records ONLY"
          [fluid]="true"
          [ngStyle]="{ 'min-height': '70px', 'field-sizing': 'content' }"
          rows="4"
          [formControl]="aiDataGenerationModal.form.controls.additionalContext"></textarea>
      </cor-input-container>
    </div>
  </div>

  <ng-template #footer>
    <p-button
      test-id="run-job-details-generate-data"
      [buttonProps]="{ autofocus: false }"
      (click)="generateAiData()"
      [disabled]="!aiDataGenerationModal.form.valid || isGeneratingAiData()"
      [loading]="isGeneratingAiData()"
      icon="pi pi-sparkles"
      [label]="isGeneratingAiData() ? 'Generating...' : 'Generate Data'" />
  </ng-template>
</cor-dialog-box>

<!-- Report Selection popup -->
<cor-dialog-box
  [(visible)]="reportDialog.isOpen"
  title="Select a report"
  [style]="{ width: '90vw' }"
  (onHiding)="closeReportSelectionModal()">
  <p-splitter
    [panelSizes]="[20, 80]"
    [gutterSize]="1"
    styleClass="tw:flex tw:border tw:border-surface  tw:mt-2">
    <!-- Sidebar filters -->
    <ng-template pTemplate>
      <div class="tw:w-full">
        <ng-container [ngTemplateOutlet]="filterTemplate" />
      </div>
    </ng-template>
    <ng-template pTemplate>
      <div class="tw:w-full tw:flex tw:flex-col">
        <p-iconfield
          iconPosition="left"
          class="tw:m-2">
          <p-inputicon class="pi pi-search" />
          <input
            test-id="run-job-details-search-reports-by-name-description-or-re"
            fluid
            pInputText
            type="search"
            placeholder="Search reports by name, description, or related terms..."
            [(ngModel)]="reportDialog.searchString" />
        </p-iconfield>
        @if (reportDialog.hasAnyFilters()) {
          <div class="tw:flex tw:items-center tw:gap-2 tw:px-2">
            <div class="tw:text-color">
              {{
                `Showing ${filteredReports().length} results out of total ${inputDataSources().reportList.length} items.`
              }}
            </div>
            <p-button
              test-id="run-job-details-clear-filters"
              [buttonProps]="{ autofocus: false }"
              (click)="clearAllFilters()"
              styleClass="tw:px-1 tw:py-0"
              variant="text"
              size="small"
              icon="pi pi-filter-slash"
              label="Clear Filters" />
          </div>
        }
        @if (filteredReports().length > 0) {
          <p-dataview
            class="tw:w-full tw:h-full report-selection-data-view"
            [value]="filteredReports()"
            [rows]="12"
            [paginator]="true">
            <ng-template
              #list
              pTemplate="list"
              let-items>
              <div class="col-12 tw:p-2 tw:grid tw:grid-cols-12 tw:gap-2">
                @for (report of items; track report.id) {
                  <div
                    tabindex="0"
                    class="tw:flex tw:flex-col tw:rounded-sm tw:gap-2 tw:p-3 tw:col-span-12 tw:md:col-span-6 tw:lg:col-span-4 tw:cursor-pointer tw:border tw:border-surface tw:text-muted-color tw:hover:bg-blue-500/5 tw:hover:border-blue-500 tw:hover:text-blue-500"
                    [attr.test-id]="'report-card-' + report.name"
                    [ngClass]="{
                      'tw:bg-blue-500/10 tw:border-blue-500 tw:text-blue-500':
                        report.id ===
                        form.controls.dataLogicFormArray.at(reportDialog.currentDataLogicIndex())
                          ?.controls?.reportVersionId.value,
                    }"
                    (click)="setSelectedReport(report.id)"
                    (keydown.enter)="setSelectedReport(report.id)"
                    (keydown.space)="setSelectedReport(report.id); $event.preventDefault()"
                    [attr.aria-label]="'Select report: ' + report.name">
                    <div class="tw:flex tw:items-center tw:gap-2">
                      <i class="pi pi-chart-scatter"></i>
                      @if (report.riskType && report.riskType !== 'other') {
                        <span class="tw:text-sm">
                          {{ $any(RISK_TYPES)[report.riskType].displayName }} Report
                        </span>
                      }
                      <a
                        test-id="run-job-details-stop-propagation"
                        class="tw:cursor-pointer tw:px-2 tw:ms-auto"
                        [class.tw:hidden]="report.url"
                        [routerLink]="report.url"
                        (click)="$event.stopPropagation()"
                        target="_blank">
                        <i class="coricon-external-link tw:text-primary"></i>
                      </a>
                    </div>
                    <div class="report-name tw:break-words tw:text-color">
                      {{ report.name | truncate: 75 }}
                    </div>
                    <small class="tw:text-muted-color tw:line-clamp-3 tw:mb-auto">
                      {{ report.description || '-' | truncate: 200 }}
                    </small>

                    <div class="tw:flex tw:justify-between">
                      <cor-status-icon [status]="report.currentStatus" />
                      @if (report.taskType && report.taskType !== 'other') {
                        <span style="font-size: 0.9em">
                          {{ $any(TASK_TYPES)[report.taskType].displayName }}
                        </span>
                      }
                    </div>
                  </div>
                }
              </div>
            </ng-template>
          </p-dataview>
        } @else {
          <cor-no-data
            class="tw:py-3"
            noDataMessage="No reports found - Try clearing filters"
            [showIcon]="true" />
        }
      </div>
    </ng-template>
  </p-splitter>
</cor-dialog-box>

<ng-template
  #reportParameterTemplate
  let-reportParametersFormArray="reportParametersFormArray"
  let-showOptionalParameters="showOptionalParameters"
  let-parameterFields="parameterFields">
  @for (reportParameterFormGroup of reportParametersFormArray; track reportParameterFormGroup) {
    <!-- Show the parameter if it is mandatory OR user wants to see optional params OR the parameter has a value -->
    @let paramAlias = reportParameterFormGroup.controls.alias.value;
    @let paramInitialValue = parameterFields[paramAlias]?.initialValue;
    @if (
      showOptionalParameters ||
      reportParameterFormGroup.controls.isMandatory.value ||
      paramInitialValue
    ) {
      <div test-id="report-parameter-form">
        <cor-input-container
          [label]="parameterFields[paramAlias]?.label || paramAlias"
          errorMsg="Please provide a parameter here"
          [tooltipText]="reportParameterFormGroup.controls.description.value"
          tooltipPosition="right"
          [horizontal]="true"
          test-id="value">
          <cor-dynamic-field
            [field]="{
              ...parameterFields[paramAlias],
              placeholder: reportParameterFormGroup.enabled
                ? parameterFields[paramAlias].description
                : `This parameter was not provided`,
            }"
            [formControl]="reportParameterFormGroup.controls.value" />
          <cor-field-error
            [visible]="
              reportParameterFormGroup.controls.value.touched &&
              reportParameterFormGroup.controls.value.invalid &&
              reportParameterFormGroup.controls.value.errors?.notValid
            "
            [errorMsg]="reportParameterFormGroup.controls.value.errors?.errorMessage" />
        </cor-input-container>
      </div>
    }
  }
</ng-template>

<ng-template #filterTemplate>
  <ng-container [ngTemplateOutlet]="projectFilter" />
  <ng-container [ngTemplateOutlet]="riskTypeFilter" />
  <ng-container [ngTemplateOutlet]="taskTypeFilter" />
  <ng-container [ngTemplateOutlet]="evaluationMethodFilter" />
  <ng-container [ngTemplateOutlet]="riskDomainFilter" />
  <ng-container [ngTemplateOutlet]="statusFilter" />
</ng-template>

<ng-template #projectFilter>
  <!-- Project Filter -->
  @if (reportDialog.filters.project.list().length > 0) {
    <div class="tw:border-b tw:border-surface tw:flex tw:flex-col">
      <div
        test-id="run-job-details-toggle-filter"
        class="tw:px-3 tw:py-2 tw:font-semibold tw:text-color tw:flex tw:justify-between tw:items-center tw:cursor-pointer"
        (click)="toggleFilter(reportDialog.filters.project)">
        <span class="tw:flex tw:items-center tw:gap-2">
          <i
            class="tw:text-sm tw:text-slate-400 pi"
            [ngClass]="{
              'pi-chevron-down': reportDialog.filters.project.isExpanded(),
              'pi-chevron-right': !reportDialog.filters.project.isExpanded(),
            }"></i>
          Project
        </span>
        @if (reportDialog.filters.project.hasSelected()) {
          <p-button
            [buttonProps]="{ autofocus: false }"
            (click)="clearFilter(reportDialog.filters.project.selectedItems, $event)"
            test-id="clear-filters"
            styleClass="tw:px-1 tw:py-0"
            variant="text"
            size="small"
            label="Clear" />
        }
      </div>
      <p-listbox
        style="
          --p-listbox-border-color: transparent;
          --p-listbox-option-padding: 0.25rem 1rem;
          --p-listbox-list-gap: 0.2rem;
          --p-listbox-shadow: transparent;
        "
        [showToggleAll]="false"
        scrollHeight="100%"
        test-id="project-filter"
        [options]="reportDialog.filters.project.list()"
        [multiple]="true"
        [checkbox]="true"
        optionLabel="displayName"
        optionValue="name"
        [(ngModel)]="reportDialog.filters.project.selectedItems"
        [ngClass]="{ 'tw:hidden': !reportDialog.filters.project.isExpanded() }">
        <ng-template
          let-item
          pTemplate="item">
          <div class="tw:flex tw:items-center tw:gap-1 tw:truncate tw:w-full">
            <span
              class="tw:text-color tw:font-semibold tw:truncate"
              [ngStyle]="{
                'font-size': '0.85em',
              }"
              [attr.test-id]="'project-filter-item-' + item.displayName">
              {{ item.displayName }}
            </span>
            <p-tag
              [style]="{ '--p-tag-font-size': '0.75rem' }"
              severity="secondary"
              [value]="item.count"
              class="tw:ms-auto tw:py-0" />
          </div>
        </ng-template>
      </p-listbox>
    </div>
  }
</ng-template>

<ng-template #riskTypeFilter>
  <!-- Risk type Filter -->
  @if (reportDialog.filters.riskType.list().length > 0) {
    <div class="tw:border-b tw:border-surface tw:flex tw:flex-col">
      <div
        test-id="run-job-details-toggle-filter-2"
        class="tw:px-3 tw:py-2 tw:font-semibold tw:text-color tw:flex tw:justify-between tw:items-center tw:cursor-pointer"
        (click)="toggleFilter(reportDialog.filters.riskType)">
        <span class="tw:flex tw:items-center tw:gap-2">
          <i
            class="tw:text-sm tw:text-slate-400 pi"
            [ngClass]="{
              'pi-chevron-down': reportDialog.filters.riskType.isExpanded(),
              'pi-chevron-right': !reportDialog.filters.riskType.isExpanded(),
            }"></i>
          Risk Type
        </span>
        @if (reportDialog.filters.riskType.hasSelected()) {
          <p-button
            [buttonProps]="{ autofocus: false }"
            (click)="clearFilter(reportDialog.filters.riskType.selectedItems, $event)"
            test-id="clear-filters"
            styleClass="tw:px-1 tw:py-0"
            variant="text"
            size="small"
            label="Clear" />
        }
      </div>
      <p-listbox
        style="
          --p-listbox-border-color: transparent;
          --p-listbox-option-padding: 0.25rem 1rem;
          --p-listbox-list-gap: 0.2rem;
          --p-listbox-shadow: transparent;
        "
        [showToggleAll]="false"
        scrollHeight="100%"
        test-id="risk-type-filter"
        [options]="reportDialog.filters.riskType.list()"
        [multiple]="true"
        [checkbox]="true"
        optionLabel="displayName"
        optionValue="name"
        [(ngModel)]="reportDialog.filters.riskType.selectedItems"
        [ngClass]="{ 'tw:hidden': !reportDialog.filters.riskType.isExpanded() }">
        <ng-template
          let-item
          pTemplate="item">
          <div class="tw:flex tw:items-center tw:gap-1 tw:truncate tw:w-full">
            <span
              class="tw:text-color tw:font-semibold tw:truncate"
              [ngStyle]="{
                'font-size': '0.85em',
              }"
              [attr.test-id]="'risk-type-filter-item-' + item.displayName">
              {{ item.displayName }}
            </span>
            <p-tag
              [style]="{ '--p-tag-font-size': '0.75rem' }"
              severity="secondary"
              [value]="item.count"
              class="tw:ms-auto tw:py-0" />
          </div>
        </ng-template>
      </p-listbox>
    </div>
  }
</ng-template>

<ng-template #taskTypeFilter>
  <!-- Task type Filter -->
  @if (reportDialog.filters.taskType.list().length > 0) {
    <div class="tw:border-b tw:border-surface tw:flex tw:flex-col">
      <div
        test-id="run-job-details-toggle-filter-3"
        class="tw:px-3 tw:py-2 tw:font-semibold tw:text-color tw:flex tw:justify-between tw:items-center tw:cursor-pointer"
        (click)="toggleFilter(reportDialog.filters.taskType)">
        <span class="tw:flex tw:items-center tw:gap-2">
          <i
            class="tw:text-sm tw:text-slate-400 pi"
            [ngClass]="{
              'pi-chevron-down': reportDialog.filters.taskType.isExpanded(),
              'pi-chevron-right': !reportDialog.filters.taskType.isExpanded(),
            }"></i>
          Task Type
        </span>
        @if (reportDialog.filters.taskType.hasSelected()) {
          <p-button
            [buttonProps]="{ autofocus: false }"
            (click)="clearFilter(reportDialog.filters.taskType.selectedItems, $event)"
            test-id="clear-filters"
            styleClass="tw:px-1 tw:py-0"
            variant="text"
            size="small"
            label="Clear" />
        }
      </div>
      <p-listbox
        style="
          --p-listbox-border-color: transparent;
          --p-listbox-option-padding: 0.25rem 1rem;
          --p-listbox-list-gap: 0.2rem;
          --p-listbox-shadow: transparent;
        "
        [showToggleAll]="false"
        scrollHeight="100%"
        test-id="task-type-filter"
        [options]="reportDialog.filters.taskType.list()"
        [multiple]="true"
        [checkbox]="true"
        optionLabel="displayName"
        optionValue="name"
        [(ngModel)]="reportDialog.filters.taskType.selectedItems"
        [ngClass]="{ 'tw:hidden': !reportDialog.filters.taskType.isExpanded() }">
        <ng-template
          let-item
          pTemplate="item">
          <div class="tw:flex tw:items-center tw:gap-1 tw:truncate tw:w-full">
            <span
              class="tw:text-color tw:font-semibold tw:truncate"
              [ngStyle]="{
                'font-size': '0.85em',
              }">
              {{ item.displayName }}
            </span>
            <p-tag
              [style]="{ '--p-tag-font-size': '0.75rem' }"
              severity="secondary"
              [value]="item.count"
              class="tw:ms-auto tw:py-0" />
          </div>
        </ng-template>
      </p-listbox>
    </div>
  }
</ng-template>

<ng-template #riskDomainFilter>
  <!-- Risk domain Filter -->
  @if (reportDialog.filters.riskDomain.list().length > 0) {
    <div class="tw:border-b tw:border-surface tw:flex tw:flex-col">
      <div
        test-id="run-job-details-toggle-filter-4"
        class="tw:px-3 tw:py-2 tw:font-semibold tw:text-color tw:flex tw:justify-between tw:items-center tw:cursor-pointer"
        (click)="toggleFilter(reportDialog.filters.riskDomain)">
        <span class="tw:flex tw:items-center tw:gap-2">
          <i
            class="tw:text-sm tw:text-slate-400 pi"
            [ngClass]="{
              'pi-chevron-down': reportDialog.filters.riskDomain.isExpanded(),
              'pi-chevron-right': !reportDialog.filters.riskDomain.isExpanded(),
            }"></i>
          Risk Domain
        </span>
        @if (reportDialog.filters.riskDomain.hasSelected()) {
          <p-button
            [buttonProps]="{ autofocus: false }"
            (click)="clearFilter(reportDialog.filters.riskDomain.selectedItems, $event)"
            test-id="clear-filters"
            styleClass="tw:px-1 tw:py-0"
            variant="text"
            size="small"
            label="Clear" />
        }
      </div>
      <p-listbox
        style="
          --p-listbox-border-color: transparent;
          --p-listbox-option-padding: 0.25rem 1rem;
          --p-listbox-list-gap: 0.2rem;
          --p-listbox-shadow: transparent;
        "
        [showToggleAll]="false"
        scrollHeight="100%"
        test-id="risk-domain-filter"
        [options]="reportDialog.filters.riskDomain.list()"
        [multiple]="true"
        [checkbox]="true"
        optionLabel="displayName"
        optionValue="name"
        [(ngModel)]="reportDialog.filters.riskDomain.selectedItems"
        [ngClass]="{ 'tw:hidden': !reportDialog.filters.riskDomain.isExpanded() }">
        <ng-template
          let-item
          pTemplate="item">
          <div class="tw:flex tw:items-center tw:gap-1 tw:truncate tw:w-full">
            <span
              class="tw:text-color tw:font-semibold tw:truncate"
              [ngStyle]="{
                'font-size': '0.85em',
              }"
              [attr.test-id]="'risk-domain-filter-item-' + item.displayName">
              {{ item.displayName }}
            </span>
            <p-tag
              [style]="{ '--p-tag-font-size': '0.75rem' }"
              severity="secondary"
              [value]="item.count"
              class="tw:ms-auto tw:py-0" />
          </div>
        </ng-template>
      </p-listbox>
    </div>
  }
</ng-template>

<ng-template #evaluationMethodFilter>
  <!-- Evaluation method Filter -->
  @if (reportDialog.filters.evaluationMethod.list().length > 0) {
    <div class="tw:border-b tw:border-surface tw:flex tw:flex-col">
      <div
        test-id="run-job-details-toggle-filter-5"
        class="tw:px-3 tw:py-2 tw:font-semibold tw:text-color tw:flex tw:justify-between tw:items-center tw:cursor-pointer"
        (click)="toggleFilter(reportDialog.filters.evaluationMethod)">
        <span class="tw:flex tw:items-center tw:gap-2">
          <i
            class="tw:text-sm tw:text-slate-400 pi"
            [ngClass]="{
              'pi-chevron-down': reportDialog.filters.evaluationMethod.isExpanded(),
              'pi-chevron-right': !reportDialog.filters.evaluationMethod.isExpanded(),
            }"></i>
          Evaluation Method
        </span>
        @if (reportDialog.filters.evaluationMethod.hasSelected()) {
          <p-button
            [buttonProps]="{ autofocus: false }"
            (click)="clearFilter(reportDialog.filters.evaluationMethod.selectedItems, $event)"
            test-id="clear-filters"
            styleClass="tw:px-1 tw:py-0"
            variant="text"
            size="small"
            label="Clear" />
        }
      </div>
      <p-listbox
        style="
          --p-listbox-border-color: transparent;
          --p-listbox-option-padding: 0.25rem 1rem;
          --p-listbox-list-gap: 0.2rem;
          --p-listbox-shadow: transparent;
        "
        [showToggleAll]="false"
        scrollHeight="100%"
        test-id="evaluation-method-filter"
        [options]="reportDialog.filters.evaluationMethod.list()"
        [multiple]="true"
        [checkbox]="true"
        optionLabel="displayName"
        optionValue="name"
        [(ngModel)]="reportDialog.filters.evaluationMethod.selectedItems"
        [ngClass]="{ 'tw:hidden': !reportDialog.filters.evaluationMethod.isExpanded() }">
        <ng-template
          let-item
          pTemplate="item">
          <div class="tw:flex tw:items-center tw:gap-1 tw:truncate tw:w-full">
            <span
              class="tw:text-color tw:font-semibold tw:truncate"
              [ngStyle]="{
                'font-size': '0.85em',
              }"
              [attr.test-id]="'evaluation-method-filter-item-' + item.displayName">
              {{ item.displayName }}
            </span>
            <p-tag
              [style]="{ '--p-tag-font-size': '0.75rem' }"
              severity="secondary"
              [value]="item.count"
              class="tw:ms-auto tw:py-0" />
          </div>
        </ng-template>
      </p-listbox>
    </div>
  }
</ng-template>

<ng-template #statusFilter>
  <!-- Status Filter -->
  @if (reportDialog.filters.status.isVisible() && reportDialog.filters.status.list().length > 0) {
    <div class="tw:border-b tw:border-surface tw:flex tw:flex-col">
      <div
        test-id="run-job-details-toggle-filter-6"
        class="tw:px-3 tw:py-2 tw:font-semibold tw:text-color tw:flex tw:justify-between tw:items-center tw:cursor-pointer"
        (click)="toggleFilter(reportDialog.filters.status)">
        <span class="tw:flex tw:items-center tw:gap-2">
          <i
            class="tw:text-sm tw:text-slate-400 pi"
            [ngClass]="{
              'pi-chevron-down': reportDialog.filters.status.isExpanded(),
              'pi-chevron-right': !reportDialog.filters.status.isExpanded(),
            }"></i>
          Status
        </span>
        @if (reportDialog.filters.status.hasSelected()) {
          <p-button
            [buttonProps]="{ autofocus: false }"
            (click)="clearFilter(reportDialog.filters.status.selectedItems, $event)"
            test-id="clear-filters"
            styleClass="tw:px-1 tw:py-0"
            variant="text"
            size="small"
            label="Clear" />
        }
      </div>
      <p-listbox
        style="
          --p-listbox-border-color: transparent;
          --p-listbox-option-padding: 0.25rem 1rem;
          --p-listbox-list-gap: 0.2rem;
          --p-listbox-shadow: transparent;
        "
        [showToggleAll]="false"
        scrollHeight="100%"
        test-id="status-filter"
        [options]="reportDialog.filters.status.list()"
        [multiple]="true"
        [checkbox]="true"
        optionLabel="displayName"
        optionValue="name"
        [(ngModel)]="reportDialog.filters.status.selectedItems"
        [ngClass]="{ 'tw:hidden': !reportDialog.filters.status.isExpanded() }">
        <ng-template
          let-item
          pTemplate="item">
          <div class="tw:flex tw:items-center tw:gap-1 tw:truncate tw:w-full">
            <cor-status-icon
              class="tw:truncate"
              [status]="item.name"
              [attr.test-id]="'status-filter-item-' + item.name" />
            <p-tag
              class="tw:ms-auto"
              severity="secondary"
              [value]="item.count"
              [style]="{ 'font-size': '0.8rem' }" />
          </div>
        </ng-template>
      </p-listbox>
    </div>
  }
</ng-template>
